<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import VButton from "../Button/VButton.vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import VIcon from "../Icon/VIcon.vue";
import { isTimeWithin, minutesToTime, nowRoundedUp, pad2, timeToMinutes } from "./dateTime";

/**
 * The content of the time picker: quick times (Nu first), then hour and minute boxes with arrows and +10 and -10,
 * then a footer with clear and OK. A quick time commits at once; the arrows and the boxes are confirmed with OK or
 * Enter. Escape cancels. Shared by VTimePicker and the time step of VDatePicker.
 */
const {
  value = null,
  minuteStep = 5,
  quickTimes = ["08:00", "09:00", "12:00", "13:00", "17:00"],
  showNow = true,
  minTime = null,
  maxTime = null,
  clearLabel,
  sheet = false,
  showHeading = true,
} = defineProps<{
  /** The time the panel starts from (`HH:mm`); without one it starts at Nu. */
  value?: string | null
  /** Step of the minute arrows and of Nu. Typed minutes stay as typed. */
  minuteStep?: number
  quickTimes?: string[]
  showNow?: boolean
  /** Earliest time that can be chosen (`HH:mm`). */
  minTime?: string | null
  /** Latest time that can be chosen (`HH:mm`). */
  maxTime?: string | null
  /** Label of the left footer button: "Geen tijd", "Annuleren" or "Wissen". */
  clearLabel: string
  /** Larger touch targets inside a bottom sheet, whose title replaces the heading. */
  sheet?: boolean
  /** The "Tijd" heading with its help; a range picker shows its start and end switch there instead. */
  showHeading?: boolean
}>();

const emit = defineEmits<{
  /** A time was chosen; `quick` for a quick time (the picker closes), `confirm` for OK or Enter. */
  commit: [time: string, source: "quick" | "confirm"]
  clear: []
  cancel: []
}>();

defineSlots<{
  /** Replaces the heading, e.g. with the start and end switch of a range. */
  heading?: () => unknown
  /** Below the boxes, e.g. the notice that the end time was adjusted. */
  notice?: () => unknown
}>();

const { t } = useI18n();

const hoursInput = ref<HTMLInputElement | null>(null);
const minutesInput = ref<HTMLInputElement | null>(null);

function seed(time: string | null): { hours: string, minutes: string } {
  const [hours, minutes] = (time && timeToMinutes(time) !== null ? time : nowRoundedUp(minuteStep)).split(":");
  return { hours: pad2(Number(hours)), minutes: pad2(Number(minutes)) };
}

const hours = ref(seed(value).hours);
const minutes = ref(seed(value).minutes);

watch(() => value, (next) => {
  const seeded = seed(next);
  hours.value = seeded.hours;
  minutes.value = seeded.minutes;
});

const hoursNumber = computed(() => (/^\d{1,2}$/.test(hours.value) ? Number(hours.value) : Number.NaN));
const minutesNumber = computed(() => (/^\d{1,2}$/.test(minutes.value) ? Number(minutes.value) : Number.NaN));

const draftValid = computed(() => hoursNumber.value >= 0
  && hoursNumber.value <= 23
  && minutesNumber.value >= 0
  && minutesNumber.value <= 59);
const draft = computed(() => (draftValid.value ? `${pad2(hoursNumber.value)}:${pad2(minutesNumber.value)}` : null));
const draftAllowed = computed(() => draft.value !== null && isTimeWithin(draft.value, minTime, maxTime));

function setDraft(totalMinutes: number): void {
  const [h, m] = minutesToTime(totalMinutes).split(":");
  hours.value = h;
  minutes.value = m;
}

function currentMinutes(): number {
  const h = Number.isNaN(hoursNumber.value) ? 0 : Math.min(hoursNumber.value, 23);
  const m = Number.isNaN(minutesNumber.value) ? 0 : Math.min(minutesNumber.value, 59);
  return h * 60 + m;
}

function hourUp(): void {
  setDraft(currentMinutes() + 60);
}

function hourDown(): void {
  setDraft(currentMinutes() - 60);
}

/** The arrows land on the minute step: 10:42 goes up to 10:45 and down to 10:40. */
function minuteUp(): void {
  const m = currentMinutes() % 60;
  setDraft(currentMinutes() + (minuteStep - (m % minuteStep)));
}

function minuteDown(): void {
  const m = currentMinutes() % 60;
  setDraft(currentMinutes() - (m % minuteStep || minuteStep));
}

function jump(delta: number): void {
  setDraft(currentMinutes() + delta);
}

function confirm(): void {
  if (!draft.value || !draftAllowed.value) {
    return;
  }
  emit("commit", draft.value, "confirm");
}

const nowTime = computed(() => nowRoundedUp(minuteStep));

const quickButtons = computed(() => {
  const buttons = quickTimes
    .filter(time => timeToMinutes(time) !== null)
    .map(time => ({ key: time, label: time, time, isNow: false }));
  if (showNow) {
    buttons.unshift({ key: "now", label: t("base.now"), time: nowTime.value, isNow: true });
  }
  return buttons.map(button => ({
    ...button,
    pressed: !button.isNow && draft.value === button.time,
    disabled: !isTimeWithin(button.time, minTime, maxTime),
  }));
});

function onBoxInput(event: Event, target: "hours" | "minutes"): void {
  const input = event.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, "").slice(0, 2);
  input.value = digits;
  if (target === "hours") {
    hours.value = digits;
    if (digits.length === 2) {
      minutesInput.value?.focus();
      minutesInput.value?.select();
    }
    return;
  }
  minutes.value = digits;
}

function onBoxKeydown(event: KeyboardEvent, target: "hours" | "minutes"): void {
  if (event.key === "ArrowUp") {
    event.preventDefault();
    (target === "hours" ? hourUp : minuteUp)();
  } else if (event.key === "ArrowDown") {
    event.preventDefault();
    (target === "hours" ? hourDown : minuteDown)();
  } else if (event.key === "Enter") {
    event.preventDefault();
    confirm();
  } else if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    emit("cancel");
  } else if (event.key === "Backspace" && target === "minutes" && (event.target as HTMLInputElement).value === "") {
    hoursInput.value?.focus();
  }
}

function focusHours(): void {
  hoursInput.value?.focus();
  hoursInput.value?.select();
}

const quickClasses = computed(() => (sheet ? "h-11 text-[15px]" : "h-8 text-[13px]"));
const stepButtonClasses = computed(() => (sheet ? "w-16 h-10" : "w-10 h-7"));
const boxClasses = computed(() => (sheet ? "w-[88px] h-[60px] text-[30px] rounded-[14px]" : "w-16 h-12 text-2xl rounded-xl"));
const jumpClasses = computed(() => (sheet ? "h-9 px-3.5 text-[13px]" : "h-6 px-[9px] text-[11px]"));

defineExpose({ focusHours, confirm });
</script>

<template>
  <div
    class="flex flex-col gap-3 text-gray-800 dark:text-gray-200"
    :class="sheet ? 'w-full' : 'w-[264px]'"
    data-test="time-panel"
  >
    <slot name="heading">
      <div v-if="showHeading && !sheet" class="flex items-center gap-1.5 text-sm leading-5 font-medium">
        {{ t("date_picker.time") }}
        <VHelpTooltip :content="t('date_picker.time_typing_help')" :subject="t('date_picker.time')" />
      </div>
    </slot>

    <div class="grid grid-cols-3 gap-1.5" role="group" :aria-label="t('date_picker.quick_times')">
      <button
        v-for="button in quickButtons"
        :key="button.key"
        type="button"
        data-test="quick-time"
        class="inline-flex items-center justify-center gap-1.5 rounded-full border px-2 font-medium tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
        :class="[
          quickClasses,
          button.pressed
            ? 'border-primary-600 bg-primary-600 text-white'
            : button.isNow
              ? 'border-primary-100 bg-primary-50 text-primary-900 enabled:hover:border-primary-300 dark:border-primary-900 dark:bg-primary-900/30 dark:text-primary-300'
              : 'border-gray-300 bg-white text-gray-800 enabled:hover:border-primary-300 enabled:hover:bg-[#F1F8E9] dark:border-dark-600 dark:bg-dark-800 dark:text-gray-200 dark:enabled:hover:bg-dark-700',
        ]"
        :aria-pressed="button.isNow ? undefined : button.pressed"
        :disabled="button.disabled"
        @click="emit('commit', button.time, 'quick')"
      >
        <VIcon v-if="button.isNow" name="clock" class="text-[13px]" aria-hidden="true" />
        {{ button.label }}
      </button>
    </div>

    <div class="flex items-center gap-2 text-xs leading-4 text-gray-500 before:flex-1 before:border-t before:border-gray-200 before:content-[''] after:flex-1 after:border-t after:border-gray-200 after:content-[''] dark:before:border-dark-600 dark:after:border-dark-600">
      {{ t("date_picker.set_precisely") }}
    </div>

    <div class="flex items-start justify-center gap-2.5">
      <div class="flex flex-col items-center gap-1">
        <span class="text-[11px] leading-[14px] font-medium tracking-[.06em] text-gray-500" aria-hidden="true">{{ t("date_picker.hour") }}</span>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-700 hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:bg-dark-700 dark:text-gray-300"
          :class="stepButtonClasses"
          :aria-label="t('date_picker.hour_up')"
          @click="hourUp"
        >
          <VIcon name="chevron-up" aria-hidden="true" />
        </button>
        <input
          ref="hoursInput"
          :value="hours"
          type="text"
          inputmode="numeric"
          maxlength="2"
          autocomplete="off"
          :aria-label="t('date_picker.hours')"
          :placeholder="t('date_picker.hour_placeholder')"
          class="border border-gray-300 bg-white p-0 text-center leading-none font-medium tabular-nums text-gray-900 outline-hidden placeholder:text-gray-400 focus:border-primary-600 focus:shadow-[0_0_0_1px] focus:shadow-primary-600 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-100"
          :class="boxClasses"
          data-test="hours-input"
          @input="onBoxInput($event, 'hours')"
          @keydown="onBoxKeydown($event, 'hours')"
          @focus="($event.target as HTMLInputElement).select()"
        >
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-700 hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:bg-dark-700 dark:text-gray-300"
          :class="stepButtonClasses"
          :aria-label="t('date_picker.hour_down')"
          @click="hourDown"
        >
          <VIcon name="chevron-down" aria-hidden="true" />
        </button>
      </div>
      <span
        class="font-medium text-gray-500"
        :class="sheet ? 'mt-9 text-[30px] leading-[60px]' : 'mt-8 text-2xl leading-[48px]'"
        aria-hidden="true"
      >
        :
      </span>
      <div class="flex flex-col items-center gap-1">
        <span class="text-[11px] leading-[14px] font-medium tracking-[.06em] text-gray-500" aria-hidden="true">{{ t("date_picker.minute") }}</span>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-700 hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:bg-dark-700 dark:text-gray-300"
          :class="stepButtonClasses"
          :aria-label="t('date_picker.minutes_later', minuteStep)"
          @click="minuteUp"
        >
          <VIcon name="chevron-up" aria-hidden="true" />
        </button>
        <input
          ref="minutesInput"
          :value="minutes"
          type="text"
          inputmode="numeric"
          maxlength="2"
          autocomplete="off"
          :aria-label="t('date_picker.minutes')"
          :placeholder="t('date_picker.minute_placeholder')"
          class="border border-gray-300 bg-white p-0 text-center leading-none font-medium tabular-nums text-gray-900 outline-hidden placeholder:text-gray-400 focus:border-primary-600 focus:shadow-[0_0_0_1px] focus:shadow-primary-600 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-100"
          :class="boxClasses"
          data-test="minutes-input"
          @input="onBoxInput($event, 'minutes')"
          @keydown="onBoxKeydown($event, 'minutes')"
          @focus="($event.target as HTMLInputElement).select()"
        >
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-700 hover:bg-gray-200 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:bg-dark-700 dark:text-gray-300"
          :class="stepButtonClasses"
          :aria-label="t('date_picker.minutes_earlier', minuteStep)"
          @click="minuteDown"
        >
          <VIcon name="chevron-down" aria-hidden="true" />
        </button>
      </div>
      <div class="flex flex-col items-center justify-center gap-1.5" :class="sheet ? 'mt-11' : 'mt-9'">
        <button
          type="button"
          class="rounded-full border border-gray-300 bg-white font-medium text-gray-700 tabular-nums hover:border-primary-300 hover:text-primary-900 focus-visible:outline-2 focus-visible:outline-primary-500 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300"
          :class="jumpClasses"
          :aria-label="t('date_picker.minutes_later', 10)"
          @click="jump(10)"
        >
          +10
        </button>
        <button
          type="button"
          class="rounded-full border border-gray-300 bg-white font-medium text-gray-700 tabular-nums hover:border-primary-300 hover:text-primary-900 focus-visible:outline-2 focus-visible:outline-primary-500 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300"
          :class="jumpClasses"
          :aria-label="t('date_picker.minutes_earlier', 10)"
          @click="jump(-10)"
        >
          -10
        </button>
      </div>
    </div>

    <slot name="notice" />

    <div class="flex items-center justify-between gap-2 border-t border-gray-200 pt-2.5 dark:border-dark-600">
      <VButton
        data-test="time-clear"
        :size="sheet ? 'medium' : 'small'"
        variant="gray"
        :class="{ 'flex-1 h-11! text-[15px]!': sheet }"
        static
        @click="emit('clear')"
      >
        {{ clearLabel }}
      </VButton>
      <VButton
        data-test="time-ok"
        :size="sheet ? 'medium' : 'small'"
        variant="primary"
        icon-left="check"
        :disabled="!draftAllowed"
        :class="{ 'flex-1 h-11! text-[15px]!': sheet }"
        static
        @click="confirm"
      >
        {{ t("base.ok") }}
      </VButton>
    </div>
  </div>
</template>
